import { PROTOCOL_VERSION } from '../../core/protocols';
import { createSeededRandom } from '../../core/random';
import { clampDifficulty } from '../../core/staircase';
import type { SessionMode, TrialPlan, TrialResult } from '../../core/types';
import type {
  ModuleFeedback,
  ResponseOption,
  RuntimeTrainingModule,
  TrainingFrameContext,
} from '../runtime';
import { estimateFixedFormThreshold } from './fixed-threshold';
import { estimateReversalThreshold } from './reversal-threshold';
import { adaptDifficultyToPerformance } from '../adaptive-difficulty';

/** Legacy names remain exported because the fixed-form queue balances these values. */
export type SweepDirection = 'contracting' | 'expanding';
export type SweepOrientation = 'vertical' | 'horizontal' | 'diagonal-up' | 'diagonal-down';
export type VisualSweepResponse = 'sweep-inward' | 'sweep-outward';
export type SweepPaletteName = 'moss' | 'cobalt' | 'amber' | 'plum';

export interface VisualSweepStimulus {
  directions: [SweepDirection, SweepDirection];
  orientation: SweepOrientation;
  sweepDurationMs: number;
  interSweepMs: number;
  /** Carrier period at the midpoint of the sweep. */
  barSpacingDegrees: number;
  /** Symmetric proportional change around the midpoint period. */
  spacingChangeRatio: number;
  contrast: number;
  gaussianSigmaAlongDegrees: number;
  gaussianSigmaAcrossDegrees: number;
  halftonePitchDegrees: number;
  carrierPhaseRadians: number;
  centerOffsetXDegrees: number;
  centerOffsetYDegrees: number;
  /** Explicitly zero: the reference is a straight Gabor carrier, never an S-curve. */
  curvatureDegrees: 0;
  palette: SweepPaletteName;
}

const ORIENTATIONS: readonly SweepOrientation[] = [
  'vertical', 'horizontal', 'diagonal-up', 'diagonal-down',
];
const TAU = Math.PI * 2;
const HALFTONE_BUCKETS = nineBuckets();
const SWEEP_PALETTE_NAMES: readonly SweepPaletteName[] = ['moss', 'cobalt', 'amber', 'plum'];
let gaborBuffer: HTMLCanvasElement | undefined;

interface SweepPalette {
  backgroundCenter: string;
  backgroundEdge: string;
  frameCenter: string;
  frameMid: string;
  frameEdge: string;
  perforation: string;
  boardBorder: string;
  boardHighlight: string;
  boardMid: string;
  boardEdge: string;
  signalLight: readonly [number, number, number];
  signalDark: readonly [number, number, number];
  signalHue: number;
  signalSaturation: number;
}

const SWEEP_PALETTES: Readonly<Record<SweepPaletteName, SweepPalette>> = {
  moss: {
    backgroundCenter: '#171c18', backgroundEdge: '#050706',
    frameCenter: '#182019', frameMid: '#111612', frameEdge: '#080b09',
    perforation: 'rgba(154, 178, 137, 0.16)', boardBorder: '#30452d',
    boardHighlight: '#789958', boardMid: '#67894c', boardEdge: '#4d6b3b',
    signalLight: [201, 220, 151], signalDark: [18, 35, 20],
    signalHue: 91, signalSaturation: 34,
  },
  cobalt: {
    backgroundCenter: '#111a22', backgroundEdge: '#04070a',
    frameCenter: '#16212b', frameMid: '#0e171e', frameEdge: '#060b0f',
    perforation: 'rgba(153, 188, 208, 0.17)', boardBorder: '#30495e',
    boardHighlight: '#7697ab', boardMid: '#5d8299', boardEdge: '#405e73',
    signalLight: [183, 216, 232], signalDark: [16, 31, 43],
    signalHue: 201, signalSaturation: 34,
  },
  amber: {
    backgroundCenter: '#20180e', backgroundEdge: '#090603',
    frameCenter: '#271d10', frameMid: '#1b130b', frameEdge: '#0d0905',
    perforation: 'rgba(216, 187, 137, 0.16)', boardBorder: '#71562f',
    boardHighlight: '#b39761', boardMid: '#9c7d4b', boardEdge: '#725932',
    signalLight: [239, 216, 166], signalDark: [48, 31, 12],
    signalHue: 38, signalSaturation: 39,
  },
  plum: {
    backgroundCenter: '#1c151f', backgroundEdge: '#080509',
    frameCenter: '#241a28', frameMid: '#18111b', frameEdge: '#0c080d',
    perforation: 'rgba(202, 174, 207, 0.16)', boardBorder: '#604766',
    boardHighlight: '#9c7ba2', boardMid: '#85658c', boardEdge: '#62496a',
    signalLight: [223, 195, 227], signalDark: [40, 20, 43],
    signalHue: 292, signalSaturation: 27,
  },
};

function nineBuckets(): number[] {
  return Array.from({ length: 9 }, (_, index) => index);
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function boundedDifficulty(difficulty: number): number {
  return clampDifficulty(difficulty, 1, 5);
}

export function difficultyToSweepDurationMs(difficulty: number): number {
  // Protocol 7.4 runs every prior level at 1.5× speed.
  return Math.round((520 - (boundedDifficulty(difficulty) - 1) * 85) / 1.5);
}

export function difficultyToInterSweepMs(difficulty: number): number {
  return Math.round(120 - (boundedDifficulty(difficulty) - 1) * 15);
}

export function difficultyToSweepSpacingDegrees(difficulty: number): number {
  return 1.04 - (boundedDifficulty(difficulty) - 1) * 0.06;
}

export function difficultyToSweepContrast(difficulty: number): number {
  return 0.18 - (boundedDifficulty(difficulty) - 1) * 0.016;
}

export function difficultyToSpacingChangeRatio(difficulty: number): number {
  return 0.38 - (boundedDifficulty(difficulty) - 1) * 0.045;
}

export function createVisualSweepStimulus(seed: number, difficulty: number): VisualSweepStimulus {
  const random = createSeededRandom(seed);
  const level = boundedDifficulty(difficulty);
  const spacing = difficultyToSweepSpacingDegrees(level);
  const contrast = difficultyToSweepContrast(level);
  return {
    directions: [
      random.boolean() ? 'contracting' : 'expanding',
      random.boolean() ? 'contracting' : 'expanding',
    ],
    orientation: random.pick(ORIENTATIONS),
    sweepDurationMs: difficultyToSweepDurationMs(level),
    interSweepMs: difficultyToInterSweepMs(level),
    barSpacingDegrees: spacing * random.range(0.94, 1.06),
    spacingChangeRatio: difficultyToSpacingChangeRatio(level) * random.range(0.96, 1.04),
    contrast: contrast * random.range(0.94, 1.03),
    // `along` is the carrier modulation axis; keeping this sigma smaller
    // makes vertical carriers form a localized patch that is taller than wide.
    gaussianSigmaAlongDegrees: random.range(1.05, 1.34),
    gaussianSigmaAcrossDegrees: random.range(1.75, 2.15),
    halftonePitchDegrees: random.range(0.115, 0.135),
    carrierPhaseRadians: random.range(-Math.PI, Math.PI),
    centerOffsetXDegrees: random.range(-0.24, 0.24),
    centerOffsetYDegrees: random.range(-0.2, 0.2),
    curvatureDegrees: 0,
    palette: SWEEP_PALETTE_NAMES[Math.abs(seed) % SWEEP_PALETTE_NAMES.length] ?? 'moss',
  };
}

/**
 * A contracting carrier continuously reduces its period (higher spatial
 * frequency); an expanding carrier does the reverse. The optional ratio lets
 * difficulty make that change subtler without changing the deterministic cue.
 */
export function sweepSpacingScale(
  direction: SweepDirection,
  progress: number,
  spacingChangeRatio = 0.38,
): number {
  const bounded = clamp(progress, 0, 1);
  const eased = bounded * bounded * (3 - 2 * bounded);
  const ratio = clamp(spacingChangeRatio, 0.08, 0.46);
  const start = direction === 'contracting' ? 1 + ratio : 1 - ratio;
  const end = direction === 'contracting' ? 1 - ratio : 1 + ratio;
  return start + (end - start) * eased;
}

export function sweepVisibility(progress: number): number {
  const bounded = clamp(progress, 0, 1);
  if (bounded === 0 || bounded === 1) return 0;
  // A long, quiet plateau keeps the carrier visible while retaining soft on/off edges.
  return Math.sin(Math.PI * bounded) ** 0.42;
}

export function visualResponseForDirection(direction: SweepDirection): VisualSweepResponse {
  return direction === 'contracting' ? 'sweep-inward' : 'sweep-outward';
}

function orientationAngle(orientation: SweepOrientation): number {
  switch (orientation) {
    case 'vertical': return 0;
    case 'horizontal': return Math.PI / 2;
    case 'diagonal-up': return Math.PI / 4;
    case 'diagonal-down': return -Math.PI / 4;
  }
}

interface SweepField {
  centerX: number;
  centerY: number;
  boardSize: number;
  boardLeft: number;
  boardTop: number;
  boardRadius: number;
}

function appendCircle(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
): void {
  context.moveTo(x + radius, y);
  context.arc(x, y, radius, 0, TAU);
}

function appendRoundedRectangle(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const boundedRadius = clamp(radius, 0, Math.min(width, height) / 2);
  context.moveTo(x + boundedRadius, y);
  context.lineTo(x + width - boundedRadius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + boundedRadius);
  context.lineTo(x + width, y + height - boundedRadius);
  context.quadraticCurveTo(x + width, y + height, x + width - boundedRadius, y + height);
  context.lineTo(x + boundedRadius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - boundedRadius);
  context.lineTo(x, y + boundedRadius);
  context.quadraticCurveTo(x, y, x + boundedRadius, y);
  context.closePath();
}

function drawPerforatedFrame(
  context: CanvasRenderingContext2D,
  frame: TrainingFrameContext,
  palette: SweepPalette,
): SweepField {
  const centerX = frame.width / 2;
  const centerY = frame.height / 2;
  const boardSize = Math.min(frame.height * 0.57, 9.5 * frame.pixelsPerDegree);
  const frameThickness = clamp(boardSize * 0.115, 34, 52);
  const outerSize = boardSize + frameThickness * 2;
  const outerLeft = centerX - outerSize / 2;
  const outerTop = centerY - outerSize / 2;
  const boardLeft = centerX - boardSize / 2;
  const boardTop = centerY - boardSize / 2;
  const outerRadius = clamp(frameThickness * 0.86, 28, 44);
  const boardRadius = clamp(boardSize * 0.048, 15, 23);

  context.save();
  context.shadowColor = 'rgba(0, 0, 0, 0.64)';
  context.shadowBlur = 28;
  context.shadowOffsetY = 12;
  context.fillStyle = '#101410';
  context.beginPath();
  appendRoundedRectangle(context, outerLeft, outerTop, outerSize, outerSize, outerRadius);
  context.fill();
  context.restore();

  const frameGradient = context.createRadialGradient(
    centerX,
    centerY,
    boardSize * 0.2,
    centerX,
    centerY,
    outerSize * 0.7,
  );
  frameGradient.addColorStop(0, palette.frameCenter);
  frameGradient.addColorStop(0.62, palette.frameMid);
  frameGradient.addColorStop(1, palette.frameEdge);
  context.fillStyle = frameGradient;
  context.beginPath();
  appendRoundedRectangle(context, outerLeft, outerTop, outerSize, outerSize, outerRadius);
  context.fill();

  const perforationPitch = clamp(frame.pixelsPerDegree * 0.16, 5, 8);
  const perforationRadius = clamp(perforationPitch * 0.18, 0.9, 1.5);
  context.save();
  context.beginPath();
  appendRoundedRectangle(context, outerLeft, outerTop, outerSize, outerSize, outerRadius);
  context.clip();
  context.beginPath();
  for (let y = outerTop + perforationPitch / 2; y < outerTop + outerSize; y += perforationPitch) {
    const row = Math.round((y - outerTop) / perforationPitch);
    const rowOffset = row % 2 === 0 ? 0 : perforationPitch / 2;
    for (let x = outerLeft + perforationPitch / 2 + rowOffset; x < outerLeft + outerSize; x += perforationPitch) {
      const onBoard = x > boardLeft - 1 && x < boardLeft + boardSize + 1
        && y > boardTop - 1 && y < boardTop + boardSize + 1;
      if (!onBoard) appendCircle(context, x, y, perforationRadius);
    }
  }
  context.fillStyle = palette.perforation;
  context.fill();
  context.restore();

  context.fillStyle = palette.boardBorder;
  context.beginPath();
  appendRoundedRectangle(
    context,
    boardLeft - 3,
    boardTop - 3,
    boardSize + 6,
    boardSize + 6,
    boardRadius + 3,
  );
  context.fill();
  const boardGradient = context.createRadialGradient(
    centerX - boardSize * 0.12,
    centerY - boardSize * 0.14,
    boardSize * 0.04,
    centerX,
    centerY,
    boardSize * 0.72,
  );
  boardGradient.addColorStop(0, palette.boardHighlight);
  boardGradient.addColorStop(0.64, palette.boardMid);
  boardGradient.addColorStop(1, palette.boardEdge);
  context.fillStyle = boardGradient;
  context.beginPath();
  appendRoundedRectangle(context, boardLeft, boardTop, boardSize, boardSize, boardRadius);
  context.fill();

  return { centerX, centerY, boardSize, boardLeft, boardTop, boardRadius };
}

function halftoneColor(level: number, palette: SweepPalette): string {
  const lightness = Math.round(clamp(43 + level * 155, 23, 68));
  return `hsl(${palette.signalHue} ${palette.signalSaturation}% ${lightness}%)`;
}

function drawSmoothGaborUnderlay(
  context: CanvasRenderingContext2D,
  frame: TrainingFrameContext,
  stimulus: VisualSweepStimulus,
  field: SweepField,
  palette: SweepPalette,
  spacingPixels: number,
  visibility: number,
  cosine: number,
  sine: number,
  waveCenterX: number,
  waveCenterY: number,
  sigmaAlong: number,
  sigmaAcross: number,
): void {
  if (typeof document === 'undefined') return;
  const sampleSize = 112;
  gaborBuffer ??= document.createElement('canvas');
  if (gaborBuffer.width !== sampleSize || gaborBuffer.height !== sampleSize) {
    gaborBuffer.width = sampleSize;
    gaborBuffer.height = sampleSize;
  }
  const bufferContext = gaborBuffer.getContext('2d');
  if (!bufferContext) return;
  const image = bufferContext.createImageData(sampleSize, sampleSize);
  const data = image.data;
  const sampleScale = field.boardSize / sampleSize;

  for (let row = 0; row < sampleSize; row += 1) {
    for (let column = 0; column < sampleSize; column += 1) {
      const x = field.boardLeft + (column + 0.5) * sampleScale;
      const y = field.boardTop + (row + 0.5) * sampleScale;
      const deltaX = x - waveCenterX;
      const deltaY = y - waveCenterY;
      const along = deltaX * cosine + deltaY * sine;
      const across = -deltaX * sine + deltaY * cosine;
      const gaussian = Math.exp(-0.5 * (
        (along * along) / (sigmaAlong * sigmaAlong)
        + (across * across) / (sigmaAcross * sigmaAcross)
      ));
      const carrier = Math.cos(TAU * along / spacingPixels + stimulus.carrierPhaseRadians);
      const signal = carrier * gaussian * visibility;
      const offset = (row * sampleSize + column) * 4;
      const light = signal >= 0;
      const color = light ? palette.signalLight : palette.signalDark;
      data[offset] = color[0];
      data[offset + 1] = color[1];
      data[offset + 2] = color[2];
      data[offset + 3] = Math.round(
        clamp(Math.abs(signal) * stimulus.contrast * 1.9, 0, 0.34) * 255,
      );
    }
  }
  bufferContext.putImageData(image, 0, 0);

  context.save();
  context.beginPath();
  context.rect(field.boardLeft, field.boardTop, field.boardSize, field.boardSize);
  context.clip();
  context.imageSmoothingEnabled = true;
  context.filter = `blur(${clamp(frame.pixelsPerDegree * 0.085, 2.5, 5)}px)`;
  context.drawImage(
    gaborBuffer,
    field.boardLeft,
    field.boardTop,
    field.boardSize,
    field.boardSize,
  );
  context.restore();
}

/** Draws a straight sinusoidal carrier sampled only by the board's dot lattice. */
function drawHalftoneGabor(
  context: CanvasRenderingContext2D,
  frame: TrainingFrameContext,
  stimulus: VisualSweepStimulus,
  field: SweepField,
  direction: SweepDirection,
  sweepElapsedMs: number,
): void {
  const progress = clamp(sweepElapsedMs / stimulus.sweepDurationMs, 0, 1);
  const visibility = sweepVisibility(progress);
  const spacingPixels = Math.max(
    5,
    stimulus.barSpacingDegrees
      * sweepSpacingScale(direction, progress, stimulus.spacingChangeRatio)
      * frame.pixelsPerDegree,
  );
  const pitch = clamp(stimulus.halftonePitchDegrees * frame.pixelsPerDegree, 4.2, 7.2);
  const baseRadius = clamp(pitch * 0.24, 1.05, 1.75);
  const sigmaAlong = stimulus.gaussianSigmaAlongDegrees * frame.pixelsPerDegree;
  const sigmaAcross = stimulus.gaussianSigmaAcrossDegrees * frame.pixelsPerDegree;
  const angle = orientationAngle(stimulus.orientation);
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);
  const waveCenterX = field.centerX + stimulus.centerOffsetXDegrees * frame.pixelsPerDegree;
  const waveCenterY = field.centerY + stimulus.centerOffsetYDegrees * frame.pixelsPerDegree;
  const bucketPoints = HALFTONE_BUCKETS.map(() => [] as Array<[number, number]>);
  const maximumLevel = Math.max(0.001, stimulus.contrast);
  const palette = SWEEP_PALETTES[stimulus.palette];

  context.save();
  context.beginPath();
  appendRoundedRectangle(
    context,
    field.boardLeft,
    field.boardTop,
    field.boardSize,
    field.boardSize,
    field.boardRadius,
  );
  context.clip();

  drawSmoothGaborUnderlay(
    context,
    frame,
    stimulus,
    field,
    palette,
    spacingPixels,
    visibility,
    cosine,
    sine,
    waveCenterX,
    waveCenterY,
    sigmaAlong,
    sigmaAcross,
  );

  for (let y = field.boardTop + pitch / 2; y < field.boardTop + field.boardSize; y += pitch) {
    const row = Math.round((y - field.boardTop) / pitch);
    const rowOffset = row % 2 === 0 ? 0 : pitch / 2;
    for (let x = field.boardLeft + pitch / 2 + rowOffset; x < field.boardLeft + field.boardSize; x += pitch) {
      const deltaX = x - waveCenterX;
      const deltaY = y - waveCenterY;
      const along = deltaX * cosine + deltaY * sine;
      const across = -deltaX * sine + deltaY * cosine;
      const gaussian = Math.exp(-0.5 * (
        (along * along) / (sigmaAlong * sigmaAlong)
        + (across * across) / (sigmaAcross * sigmaAcross)
      ));
      const carrier = Math.cos(TAU * along / spacingPixels + stimulus.carrierPhaseRadians);
      const level = carrier * gaussian * stimulus.contrast * visibility;
      const normalized = clamp(level / maximumLevel, -1, 1);
      const bucket = Math.round(((normalized + 1) / 2) * (HALFTONE_BUCKETS.length - 1));
      bucketPoints[bucket]?.push([x, y]);
    }
  }

  for (const bucket of HALFTONE_BUCKETS) {
    const points = bucketPoints[bucket];
    if (!points?.length) continue;
    const normalized = bucket / (HALFTONE_BUCKETS.length - 1) * 2 - 1;
    const level = normalized * stimulus.contrast;
    const radius = baseRadius * (1 + normalized * 0.09);
    context.beginPath();
    for (const [x, y] of points) appendCircle(context, x, y, radius);
    context.fillStyle = halftoneColor(level, palette);
    context.globalAlpha = 0.86;
    context.fill();
  }
  context.globalAlpha = 1;

  const vignette = context.createRadialGradient(
    field.centerX,
    field.centerY,
    field.boardSize * 0.23,
    field.centerX,
    field.centerY,
    field.boardSize * 0.72,
  );
  vignette.addColorStop(0, 'rgba(20, 34, 20, 0)');
  vignette.addColorStop(0.68, 'rgba(20, 34, 20, 0.035)');
  vignette.addColorStop(1, 'rgba(8, 14, 9, 0.22)');
  context.fillStyle = vignette;
  context.fillRect(field.boardLeft, field.boardTop, field.boardSize, field.boardSize);
  context.restore();
}

function renderVisualSweeps(
  context: CanvasRenderingContext2D,
  frame: TrainingFrameContext,
  trial: TrialPlan<VisualSweepStimulus>,
): void {
  context.save();
  context.setTransform(frame.devicePixelRatio, 0, 0, frame.devicePixelRatio, 0, 0);
  const stimulus = trial.stimulus;
  const palette = SWEEP_PALETTES[stimulus.palette];
  const background = context.createRadialGradient(
    frame.width / 2,
    frame.height / 2,
    0,
    frame.width / 2,
    frame.height / 2,
    Math.max(frame.width, frame.height) * 0.65,
  );
  background.addColorStop(0, palette.backgroundCenter);
  background.addColorStop(1, palette.backgroundEdge);
  context.fillStyle = background;
  context.fillRect(0, 0, frame.width, frame.height);

  const field = drawPerforatedFrame(context, frame, palette);
  const firstEnd = stimulus.sweepDurationMs;
  const secondStart = firstEnd + stimulus.interSweepMs;
  const secondEnd = secondStart + stimulus.sweepDurationMs;
  if (frame.elapsed < firstEnd) {
    drawHalftoneGabor(context, frame, stimulus, field, stimulus.directions[0], frame.elapsed);
  } else if (frame.elapsed >= secondStart && frame.elapsed < secondEnd) {
    drawHalftoneGabor(
      context,
      frame,
      stimulus,
      field,
      stimulus.directions[1],
      frame.elapsed - secondStart,
    );
  } else {
    // The carrier is absent between sweeps, but the same dotted surface remains visible.
    drawHalftoneGabor(context, frame, stimulus, field, stimulus.directions[0], 0);
  }
  context.restore();
}

function directionOptions(step: 1 | 2): readonly ResponseOption[] {
  return [
    {
      value: `${step}:sweep-inward`,
      keys: ['ArrowLeft'],
      keyLabel: '←',
      label: '内向き（間隔が狭くなる）',
      visual: 'sweep-inward',
    },
    {
      value: `${step}:sweep-outward`,
      keys: ['ArrowRight'],
      keyLabel: '→',
      label: '外向き（間隔が広くなる）',
      visual: 'sweep-outward',
    },
  ];
}

export function adaptVisualSweepDifficulty(current: number, recentResults: TrialResult[]): number {
  return adaptDifficultyToPerformance(current, recentResults);
}

export const readSwitchModule: RuntimeTrainingModule<VisualSweepStimulus> = {
  id: 'read-switch',
  label: '2回の動きの向きを見分ける',
  shortLabel: '空間周波数スイープ識別',
  protocolVersion: PROTOCOL_VERSION,
  evidenceLabel: '製品化課題を参考にした構成',
  instructions: [
    'セットごとに色が変わるドット面に、ぼんやりした明暗の波が2回現れます。',
    '波の間隔が狭くなる「内向き」か、広くなる「外向き」かを順番に答えます。',
    '正答が続くと、波の間隔変化・コントラスト・表示時間が少しずつ難しくなります。',
  ],
  responseKind: 'multi-select',
  selectionCount: 2,
  autoSubmitMultiSelect: true,
  serializeSelections(selected): string {
    return selected.join(',');
  },
  createTrial(seed: number, difficulty: number, mode: SessionMode): TrialPlan<VisualSweepStimulus> {
    const bounded = boundedDifficulty(difficulty);
    const stimulus = createVisualSweepStimulus(seed, bounded);
    const first = visualResponseForDirection(stimulus.directions[0]);
    const second = visualResponseForDirection(stimulus.directions[1]);
    return {
      id: `visual-sweeps-${mode}-${seed}`,
      moduleId: 'read-switch',
      protocolVersion: PROTOCOL_VERSION,
      seed,
      stimulusId: `vs-gabor-${first}-${second}-${stimulus.orientation}-${stimulus.sweepDurationMs}-${seed}`,
      stimulus,
      correctResponse: `1:${first},2:${second}`,
      timings: {
        fixationMs: 500,
        stimulusMs: stimulus.sweepDurationMs * 2 + stimulus.interSweepMs,
        responseWindowMs: 5000,
      },
      difficulty: bounded,
    };
  },
  render: renderVisualSweeps,
  isResponseAllowed(response: string): boolean {
    return /^1:sweep-(inward|outward),2:sweep-(inward|outward)$/.test(response);
  },
  adaptDifficulty: adaptVisualSweepDifficulty,
  getResponseOptions(_trial, selected = []): readonly ResponseOption[] {
    return directionOptions(selected.length === 0 ? 1 : 2);
  },
  getResponsePrompt(_trial, selected): string {
    return selected.length === 0 ? '1回目の波は？' : '2回目の波は？';
  },
  getFeedback(trial, response, correct): ModuleFeedback {
    const readable = (direction: SweepDirection) => direction === 'contracting' ? '内向き' : '外向き';
    const input = response
      ?.replaceAll('sweep-inward', '内向き')
      .replaceAll('sweep-outward', '外向き');
    return {
      title: correct ? '2回とも一致しました' : '波の間隔変化を確認してください',
      detail: input ? `入力：${input}` : '制限時間内に2回答えられませんでした。',
      correctLabel: `1回目 ${readable(trial.stimulus.directions[0])} / 2回目 ${readable(trial.stimulus.directions[1])}`,
    };
  },
  getResultMetadata(trial) {
    const firstDirection = visualResponseForDirection(trial.stimulus.directions[0]);
    const secondDirection = visualResponseForDirection(trial.stimulus.directions[1]);
    return {
      sweepDurationMs: trial.stimulus.sweepDurationMs,
      interSweepMs: trial.stimulus.interSweepMs,
      firstDirection,
      secondDirection,
      orientation: trial.stimulus.orientation,
      barSpacingDegrees: trial.stimulus.barSpacingDegrees,
      spacingChangeRatio: trial.stimulus.spacingChangeRatio,
      spatialFrequencyCyclesPerDegree: 1 / trial.stimulus.barSpacingDegrees,
      contrast: trial.stimulus.contrast,
      gaussianSigmaAlongDegrees: trial.stimulus.gaussianSigmaAlongDegrees,
      gaussianSigmaAcrossDegrees: trial.stimulus.gaussianSigmaAcrossDegrees,
      halftonePitchDegrees: trial.stimulus.halftonePitchDegrees,
      curvatureDegrees: trial.stimulus.curvatureDegrees,
      visualStyle: 'localized-halftone-gabor',
      responseInwardValue: 'sweep-inward',
      responseOutwardValue: 'sweep-outward',
      referenceProduct: 'BrainHQ Visual Sweeps',
    };
  },
  getThreshold(results) {
    const isFixedAssessment = results.some(
      (result) => result.moduleId === 'read-switch' && result.metadata.assessment === true,
    );
    const fixed = estimateFixedFormThreshold(results, {
      moduleId: 'read-switch',
      metadataKey: 'sweepDurationMs',
      direction: 'increasing',
    });
    if (fixed !== undefined) return { value: fixed, unit: 'ms' };
    if (isFixedAssessment) return undefined;
    const value = estimateReversalThreshold(results, {
      moduleId: 'read-switch',
      metadataKey: 'sweepDurationMs',
      lastReversals: 6,
    });
    return value === undefined ? undefined : { value, unit: 'ms' };
  },
};
